import React, { useCallback, useContext, useRef, useMemo, useState, useEffect } from 'react';
import type { FC } from 'react';

import { useQuery } from '@atlassian/ufo-apollo-log/use-query';

import { NewCommentsContext } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import type {
	CommentsSectionQuery as CommentsSectionQueryType,
	CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType,
	CommentsSectionQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionQuery.ts';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql.ts';
import { DEFAULT_LIMIT } from '@confluence/page-comments-queries';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery.ts';
import { useIsReactionsEnabled } from '@confluence/comments-hooks/entry-points/useIsReactionsEnabled.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import {
	MissedPubSubCommentEventsHandler,
	CommentEventType,
	type MissedCommentsResult,
} from '../MissedPubSubCommentEventsHandler.ts';

import { setUpPageCommentsPubSub, removePageCommentsPubSub } from './pageCommentsPubSub.ts';
import { reloadAndAddPageComments } from './pageCommentsOperations.ts';

type PageCommentsPubSubHandlerProps = {
	contentId: string;
	contentType?: string | null;
};

export const PageCommentsPubSubHandler: FC<PageCommentsPubSubHandlerProps> = ({
	contentId,
	contentType,
}) => {
	const isReactionsEnabled = useIsReactionsEnabled(contentId);
	const { addNewComment, removeComment, clearNewComments } = useContext(NewCommentsContext);
	const { addSubscriber, removeSubscriber, setContentType, setContentId } =
		useSubscriptionsDispatchContext() || {};
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);
	const { userId } = useSessionData();

	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: contentId });
	const pageMode = useGetPageMode();

	const {
		addReplyToCommentThread,
		addNewCommentThreads,
		getCurrentCommentsPanelState,
		updateCommentBody,
		updateRemovedCommentIdsMap,
		reopenParentComment,
	} = useCommentsDataActions();

	// Control skip state - start with true to prevent query on mount, set to false when events are detected
	const [shouldSkipQuery, setShouldSkipQuery] = useState(true);

	// Reset skip state when contentId changes (new page)
	useEffect(() => {
		setShouldSkipQuery(true);
	}, [contentId]);

	const { deleteCommentInCache, addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// Always call both hooks to follow React rules, but skip the queries conditionally
	// Legacy implementation using CommentsSectionQuery
	const legacyQueryResult = useQuery<
		CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType,
		CommentsSectionQueryVariables
	>(isReactionsEnabled ? CommentsSectionQuery : CommentsSectionWithoutReactionsQuery, {
		variables: {
			contentId,
			offset: '',
			first: DEFAULT_LIMIT,
		},
		fetchPolicy: 'cache-and-network',
		skip:
			!contentId ||
			expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true),
	});
	// New implementation using ActiveCommentsQuery
	// Use 'cache-only' to prevent automatic refetches on re-render/remount
	// Skip is controlled by state - starts as true, set to false when handleEventFetchSuccess is called
	const { refetch: activeCommentsRefetch } = useActiveCommentsQuery({
		pageId: contentId,
		type: 'FOOTER',
		skip:
			// TODO: Remove this once the experiment confluence_comment_use_active_comment_query_exp is concluded
			shouldSkipQuery ||
			!expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true) ||
			!contentId,
		fetchPolicy: 'cache-and-network',
	});

	// Use the appropriate refetch function based on experiment
	const refetch = useMemo(() => {
		return expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true)
			? activeCommentsRefetch
			: legacyQueryResult.refetch;
	}, [activeCommentsRefetch, legacyQueryResult.refetch]);

	const subscribeToPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - do not setup pubsub for edit pages
		if (
			pageMode === PageMode.EDIT &&
			expValEquals('confluence_comment_footercomments_editpage_exp', 'isEnabled', true)
		) {
			return;
		}

		if (
			subscriptions.current.length > 0 &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		subscriptions.current =
			setUpPageCommentsPubSub({
				contentId,
				contentType,
				userId,
				reloader: refetch,
				addNewComment,
				removeComment,
				queryArgs,
				addNewCommentThreads,
				addReplyToCommentThread,
				updateCommentBody,
				updateRemovedCommentIdsMap,
				reopenParentComment,
				getCurrentCommentsPanelState,
				addSubscriber,
				setContentType,
				setContentId,
				updateUnreadCommentIDSet,
				getViewedCommentsCacheOnly,
				addCommentInCache,
				deleteCommentInCache,
			}) ?? [];
	}, [
		contentId,
		contentType,
		userId,
		refetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		addNewCommentThreads,
		addReplyToCommentThread,
		updateCommentBody,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		getCurrentCommentsPanelState,
		addSubscriber,
		setContentType,
		setContentId,
		updateUnreadCommentIDSet,
		getViewedCommentsCacheOnly,
		addCommentInCache,
		deleteCommentInCache,
		pageMode,
	]);

	const unsubscribeFromPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - no pubsub was set up for edit pages
		if (
			pageMode === PageMode.EDIT &&
			expValEquals('confluence_comment_footercomments_editpage_exp', 'isEnabled', true)
		) {
			return;
		}

		if (
			subscriptions.current.length === 0 &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		removePageCommentsPubSub({
			contentId,
			contentType,
			userId,
			reloader: refetch,
			addNewComment,
			removeComment,
			queryArgs,
			updateRemovedCommentIdsMap,
			removeSubscriber,
			subscriptions: subscriptions.current,
			getViewedCommentsCacheOnly,
		});

		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			subscriptions.current = [];
		}
	}, [
		contentId,
		contentType,
		userId,
		refetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		updateRemovedCommentIdsMap,
		removeSubscriber,
		getViewedCommentsCacheOnly,
		pageMode,
	]);

	const handleEventFetchSuccess = useCallback(
		(data: MissedCommentsResult) => {
			if (data && data.contentId === contentId) {
				// Enable the query so refetch can work
				setShouldSkipQuery(false);
				if (data.commentEvents.length) {
					const commentData = data.commentEvents.map((event) => {
						return { commentId: event.id, accountId: event.accountId };
					});
					void reloadAndAddPageComments(refetch, addNewComment, commentData, userId);
				}
			}
		},
		[contentId, addNewComment, refetch, userId],
	);

	if (!userId) {
		return null;
	} else {
		return (
			<ErrorBoundary attribution={Attribution.COLLABORATION}>
				<MissedPubSubCommentEventsHandler
					contentId={contentId}
					eventType={CommentEventType.FOOTER}
					subscribeToPubSubEvents={subscribeToPageCommentsPubSub}
					unsubscribeFromPubSubEvents={unsubscribeFromPageCommentsPubSub}
					onEventFetchSuccess={handleEventFetchSuccess}
					onDocumentReturn={clearNewComments}
					isAutoReloadPageComments
				/>
			</ErrorBoundary>
		);
	}
};
